import type { MutableRefObject, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Drawer } from '@noths/polaris-client-ribbons-design-system';

import * as copy from 'src/components/organisms/Personalisation/constants/copy';
import type { MediaItem } from 'src/components/organisms/ProductMediaCarousel/types';
import type { ProductOptionsDrawerFooterProps } from './ProductOptionsDrawerFooter';
import { ProductOptionsDrawerFooter } from './ProductOptionsDrawerFooter';
import { ProductOptionsDrawerImages } from './ProductOptionsDrawerImages';
import * as styles from './ProductOptionsDrawer.styles';

/** The footer's props are passed straight through, so they are declared once, on the footer. */
export interface ProductOptionsDrawerViewProps extends ProductOptionsDrawerFooterProps {
  /** Null for a product whose drawer needs no title, e.g. a gift card. */
  heading: string | null;
  isOpen: boolean;
  media: MediaItem[];
  onClose: () => void;
  onVisible: () => void;
  /**
   * A slot for the same reason as `expressCheckout`: the options form reads and writes the store,
   * so a story supplies its own stand-in rather than a live container.
   */
  personalisation: ReactNode;
  /** The CTA column the drawer aligns its left edge to */
  target: MutableRefObject<HTMLDivElement | null>;
}

const isMobileDevice = () => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

/**
 * The options drawer's presentation, with the two store-bound subtrees left as slots.
 *
 * Pure, so the states a shopper moves through here can be seen in Storybook without a store or a
 * product that happens to have the right options. ProductOptionsDrawer holds the hooks that fill
 * these props.
 */
export const ProductOptionsDrawerView = ({
  basketCta,
  expressCheckout,
  heading,
  isOpen,
  media,
  onClose,
  onVisible,
  personalisation,
  priceBreakdown,
  quantitySelector,
  target,
}: ProductOptionsDrawerViewProps) => {
  const [width, setWidth] = useState(0);
  const contentRef = useImpressionRef<HTMLDivElement>(() => onVisible());

  useEffect(() => {
    const updateWidth = () => {
      const leftPos = target.current?.getBoundingClientRect().left;
      const currentWidth = isMobileDevice()
        ? window.screen.width - leftPos!
        : window.innerWidth - leftPos!;
      setWidth(currentWidth);
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer
      contentContainerStyles={styles.drawerContainer}
      contentLabel={copy.CONTENT_LABEL}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <section css={styles.section} style={{ width }}>
        <header css={styles.header}>
          <button
            aria-expanded={isOpen}
            aria-label="Close"
            css={styles.closeButton}
            onClick={onClose}
          />
        </header>
        <div css={styles.content} ref={contentRef}>
          <ProductOptionsDrawerImages media={media} />
          {heading && <h2 css={styles.heading}>{heading}</h2>}
          <form noValidate>{personalisation}</form>
        </div>
        <ProductOptionsDrawerFooter
          basketCta={basketCta}
          expressCheckout={expressCheckout}
          priceBreakdown={priceBreakdown}
          quantitySelector={quantitySelector}
        />
      </section>
    </Drawer>
  );
};
